import Phaser from 'phaser';
import { itemManager } from '../../managers/ItemManager';
import { inventoryManager } from '../../managers/InventoryManager';

export default class ToolUI extends Phaser.GameObjects.Container {
    constructor(scene, x = 0, y = 0) {
        super(scene, x, y);
        this.scene = scene;
        
        // Debug mode state
        this.debugMode = false;
        
        // Tool slot states (display only)
        this.equippedSlot1 = null; // E key slot - for display
        this.equippedSlot2 = null; // F key slot - for display
        
        // Quantity text for seedbags
        this.slot1QuantityText = null;
        this.slot2QuantityText = null;
        
        // UI dimensions and positions
        this.viewportWidth = 256;
        this.viewportHeight = 192;
        this.toolUIWidth = 77;
        this.toolUIHeight = 44;
        this.slotSize = 27;
        
        // Calculate positions (tool-ui.png centered at bottom)
        this.toolUIX = this.viewportWidth / 2 + 90; // 219
        this.toolUIY = 170;
        
        // Tool slot positions (relative to tool UI center)
        this.slot1X = this.toolUIX - 16; // adjusted based on toolUIX
        this.slot1Y = this.toolUIY;
        this.slot2X = this.toolUIX + 16; // adjusted based on toolUIX
        this.slot2Y = this.toolUIY;
        
        // Transparency control
        this.currentAlpha = 1.0;
        this.targetAlpha = 1.0;
        this.isTransparent = false;
        
        this.createUI();
        this.setupTransparencySystem();
        this.setupTooltip();
        this.loadEquipmentState();
        
        scene.add.existing(this);
        
        // Set high depth to appear on top and fix to screen coordinates
        this.setDepth(500);
        this.setScrollFactor(0);
    }
    
    createUI() {
        // Create the main tool UI background (positioned relative to container)
        this.toolUIBg = this.scene.add.image(this.toolUIX, this.toolUIY, 'tool-ui');
        this.add(this.toolUIBg);
        
        // Create hover overlays (initially invisible, positioned relative to container)
        this.slot1Hover = this.scene.add.image(this.slot1X, this.slot1Y, 'tool-ui-icon-empty-hover');
        this.slot1Hover.setVisible(false);
        this.slot1Hover.setDepth(508); // Above UI (500) but below equipped tools (510)
        this.slot1Hover.setScrollFactor(0);
        
        this.slot2Hover = this.scene.add.image(this.slot2X, this.slot2Y, 'tool-ui-icon-empty-hover');
        this.slot2Hover.setVisible(false);
        this.slot2Hover.setDepth(508); // Above UI (500) but below equipped tools (510)
        this.slot2Hover.setScrollFactor(0);
        
        // Create debug highlight rectangles (initially invisible)
        this.debugGraphics = this.scene.add.graphics();
        this.debugGraphics.setScrollFactor(0); // Fix to screen coordinates
        this.debugGraphics.setDepth(550); // Below interactive areas but above UI
        this.updateDebugVisuals();
        
        // Create interactive areas for equipped tool slots
        this.createSlotInteractiveAreas();
    }
    
    createSlotInteractiveAreas() {
        // Create invisible interactive areas over tool slots
        this.slot1Area = this.scene.add.rectangle(this.slot1X, this.slot1Y, this.slotSize, this.slotSize, 0x000000, 0);
        this.slot1Area.setInteractive();
        this.slot1Area.setDepth(512); // Above equipped tools (510) for interaction
        this.slot1Area.setScrollFactor(0);
        this.slot1Area.slotNumber = 1;
        
        this.slot2Area = this.scene.add.rectangle(this.slot2X, this.slot2Y, this.slotSize, this.slotSize, 0x000000, 0);
        this.slot2Area.setInteractive();
        this.slot2Area.setDepth(512); // Above equipped tools (510) for interaction
        this.slot2Area.setScrollFactor(0);
        this.slot2Area.slotNumber = 2;
        
        // Setup hover events
        this.setupHoverEvents();
        
        // Setup click events
        this.setupClickEvents();
    }
    
    setupHoverEvents() {
        // Slot 1 hover events
        this.slot1Area.on('pointerover', () => {
            if (this.equippedSlot1) { // Only show hover if there's an equipped tool
            this.slot1Hover.setVisible(true);
                
                // Show tooltip for seedbags
                if (this.equippedSlot1.startsWith('Seeds_')) {
                    const seedData = this.getSeedDataById(this.equippedSlot1);
                    if (seedData) {
                        this.showTooltip(this.slot1Icon, seedData.name, 1);
                    }
                } else if (this.equippedSlot1.startsWith('Tool_')) {
                    // Show tooltip for tools
                    const toolText = this.getToolTooltipText(this.equippedSlot1);
                    this.showTooltip(this.slot1Icon, toolText, 1);
                }
            }
        });
        
        this.slot1Area.on('pointerout', () => {
            this.slot1Hover.setVisible(false);
            this.tooltip.setVisible(false);
        });
        
        // Slot 2 hover events
        this.slot2Area.on('pointerover', () => {
            if (this.equippedSlot2) { // Only show hover if there's an equipped tool
            this.slot2Hover.setVisible(true);
                
                // Show tooltip for seedbags
                if (this.equippedSlot2.startsWith('Seeds_')) {
                    const seedData = this.getSeedDataById(this.equippedSlot2);
                    if (seedData) {
                        this.showTooltip(this.slot2Icon, seedData.name, 2);
                    }
                } else if (this.equippedSlot2.startsWith('Tool_')) {
                    // Show tooltip for tools
                    const toolText = this.getToolTooltipText(this.equippedSlot2);
                    this.showTooltip(this.slot2Icon, toolText, 2);
                }
            }
        });
        
        this.slot2Area.on('pointerout', () => {
            this.slot2Hover.setVisible(false);
            this.tooltip.setVisible(false);
        });
    }
    
    setupClickEvents() {
        // Slot 1 click events
        this.slot1Area.on('pointerdown', (pointer) => {
            if (pointer.rightButtonDown() && this.equippedSlot1) {
                pointer.event.preventDefault(); // Prevent browser context menu
                this.unequipTool(1);
            }
        });
        
        // Slot 2 click events
        this.slot2Area.on('pointerdown', (pointer) => {
            if (pointer.rightButtonDown() && this.equippedSlot2) {
                pointer.event.preventDefault(); // Prevent browser context menu
                this.unequipTool(2);
            }
        });
    }
    
    unequipTool(slotNumber) {
        //console.log(`Unequipping tool from slot ${slotNumber}`);
        
        if (slotNumber === 1) {
            this.equippedSlot1 = null;
            this.slot1Hover.setVisible(false);
            if (this.slot1Icon) {
                this.slot1Icon.destroy();
                this.slot1Icon = null;
            }
            if (this.slot1QuantityText) {
                this.slot1QuantityText.destroy();
                this.slot1QuantityText = null;
            }
        } else if (slotNumber === 2) {
            this.equippedSlot2 = null;
            this.slot2Hover.setVisible(false);
            if (this.slot2Icon) {
                this.slot2Icon.destroy();
                this.slot2Icon = null;
            }
            if (this.slot2QuantityText) {
                this.slot2QuantityText.destroy();
                this.slot2QuantityText = null;
            }
        }
        
        // Save to backend when implemented
        this.saveEquipmentState(slotNumber, null);
        
        // Update debug display
        this.updateDebugVisuals();
    }
    
    // Load equipment state from backend
    async loadEquipmentState() {
        try {
            const gameService = window.gameService;
            if (!gameService) {
                console.warn('GameService not available, cannot load equipment state');
                return;
            }

            const userOptions = await gameService.getUserOptions();
            
            // Check for duplicate tools and prevent them
            let slot1Tool = userOptions.toolslot_1_equipped;
            let slot2Tool = userOptions.toolslot_2_equipped;
            
            if (slot1Tool && slot2Tool && slot1Tool === slot2Tool) {
                //console.log(`Duplicate tool detected: ${slot1Tool} in both slots. Keeping in slot 1, clearing slot 2.`);
                slot2Tool = null;
                
                // Update database to clear slot 2
                try {
                    await gameService.updateUserOptions({ toolslot_2_equipped: null });
                    //console.log('Cleared duplicate tool from slot 2 in database');
                } catch (error) {
                    console.error('Failed to clear duplicate tool from database:', error);
                }
            }
            
            // Load equipped tools from user options
            if (slot1Tool) {
                this.updateEquippedTool(1, slot1Tool);
            }
            
            if (slot2Tool) {
                this.updateEquippedTool(2, slot2Tool);
            }
            
            // console.log('ToolUI: Loaded equipment state from database:', {
            //     slot1: this.equippedSlot1,
            //     slot2: this.equippedSlot2
            // });
            
            //console.log('ToolUI: Equipment icons should now be visible');
            
        } catch (error) {
            console.error('Error loading equipment state:', error);
        }
    }

    // Save equipment state to backend
    async saveEquipmentState(slotNumber, itemCode) {
        try {
            const gameService = window.gameService;
            if (!gameService) {
                console.warn('GameService not available, cannot save equipment state');
                return;
            }

            //console.log(`Saving tool equipment: slot ${slotNumber} = ${itemCode || 'empty'}`);
        
            // Create update object with only the relevant slot
            const updateData = {};
            if (slotNumber === 1) {
                updateData.toolslot_1_equipped = itemCode;
            } else if (slotNumber === 2) {
                updateData.toolslot_2_equipped = itemCode;
            }

            // Update user options
            await gameService.updateUserOptions(updateData);
            //console.log(`Successfully saved equipment state for slot ${slotNumber}:`, updateData);
            
        } catch (error) {
            console.error('Error saving equipment state:', error);
        }
    }
    
    updateDebugVisuals() {
        this.debugGraphics.clear();
        
        if (this.debugMode) {
            // Draw purple debug rectangles over tool slots
            this.debugGraphics.lineStyle(2, 0x800080); // Purple border
            this.debugGraphics.fillStyle(0x800080, 0.3); // Purple fill with transparency
            
            // Slot 1 debug rectangle (positioned same as interactive area)
            const slot1Rect = new Phaser.Geom.Rectangle(
                this.slot1X - this.slotSize / 2,
                this.slot1Y - this.slotSize / 2,
                this.slotSize,
                this.slotSize
            );
            this.debugGraphics.strokeRectShape(slot1Rect);
            this.debugGraphics.fillRectShape(slot1Rect);
            
            // Slot 2 debug rectangle (positioned same as interactive area)
            const slot2Rect = new Phaser.Geom.Rectangle(
                this.slot2X - this.slotSize / 2,
                this.slot2Y - this.slotSize / 2,
                this.slotSize,
                this.slotSize
            );
            this.debugGraphics.strokeRectShape(slot2Rect);
            this.debugGraphics.fillRectShape(slot2Rect);
            
            // Add debug text
            if (!this.debugText) {
                this.debugText = this.scene.add.text(10, 10, '', {
                    fontFamily: 'Arial',
                    fontSize: '12px',
                    color: '#800080'
                });
                this.debugText.setScrollFactor(0);
                this.debugText.setDepth(600);
            }
            
            this.debugText.setText([
                'Tool UI Debug Mode (Display Only)',
                `Slot 1: E key (${this.slot1X}, ${this.slot1Y}) - ${this.equippedSlot1 || 'empty'}`,
                `Slot 2: F key (${this.slot2X}, ${this.slot2Y}) - ${this.equippedSlot2 || 'empty'}`,
                `Transparent: ${this.isTransparent} (α=${this.currentAlpha.toFixed(1)})`,
                `Camera: (${Math.round(this.scene.cameras.main.scrollX)}, ${Math.round(this.scene.cameras.main.scrollY)})`,
                `Player: ${this.scene.player ? `(${Math.round(this.scene.player.x)}, ${Math.round(this.scene.player.y)})` : 'none'}`
            ]);
            this.debugText.setVisible(true);
        } else {
            if (this.debugText) {
                this.debugText.setVisible(false);
            }
        }
    }
    
    // Method to update equipped tool display
    updateEquippedTool(slotNumber, toolItemCode) {
        //console.log(`ToolUI: updateEquippedTool called - slot ${slotNumber}, tool ${toolItemCode}`);
        
        // Prevent duplicate tools - if tool is already equipped in other slot, unequip it
        if (toolItemCode) {
            if (slotNumber === 1 && this.equippedSlot2 === toolItemCode) {
                //console.log(`Tool ${toolItemCode} already equipped in slot 2, unequipping from slot 2`);
                this.unequipTool(2);
            } else if (slotNumber === 2 && this.equippedSlot1 === toolItemCode) {
                //console.log(`Tool ${toolItemCode} already equipped in slot 1, unequipping from slot 1`);
                this.unequipTool(1);
            }
        }
        if (slotNumber === 1) {
            // Remove existing slot 1 icon and quantity text if present
            if (this.slot1Icon) {
                this.slot1Icon.destroy();
                this.slot1Icon = null;
            }
            if (this.slot1QuantityText) {
                this.slot1QuantityText.destroy();
                this.slot1QuantityText = null;
            }
            
            this.equippedSlot1 = toolItemCode;
            
            // Create new icon if tool/seed is equipped
            if (toolItemCode) {
                this.slot1Icon = this.createEquippedIcon(toolItemCode, this.slot1X, this.slot1Y);
                
                // Create quantity text for seedbags
                if (toolItemCode && toolItemCode.startsWith('Seeds_')) {
                    this.slot1QuantityText = this.createQuantityText(toolItemCode, this.slot1X + 10, this.slot1Y - 10);
                    
                    // Refresh quantity immediately to show current inventory
                    const currentQuantity = this.getItemQuantity(toolItemCode);
                    this.slot1QuantityText.setText(currentQuantity.toString());
                }
            }
            
        } else if (slotNumber === 2) {
            // Remove existing slot 2 icon and quantity text if present
            if (this.slot2Icon) {
                this.slot2Icon.destroy();
                this.slot2Icon = null;
            }
            if (this.slot2QuantityText) {
                this.slot2QuantityText.destroy();
                this.slot2QuantityText = null;
            }
            
            this.equippedSlot2 = toolItemCode;
            
            // Create new icon if tool/seed is equipped
            if (toolItemCode) {
                this.slot2Icon = this.createEquippedIcon(toolItemCode, this.slot2X, this.slot2Y);
                
                // Create quantity text for seedbags
                if (toolItemCode && toolItemCode.startsWith('Seeds_')) {
                    this.slot2QuantityText = this.createQuantityText(toolItemCode, this.slot2X + 10, this.slot2Y - 10);
                    
                    // Refresh quantity immediately to show current inventory
                    const currentQuantity = this.getItemQuantity(toolItemCode);
                    this.slot2QuantityText.setText(currentQuantity.toString());
                }
            }
        }
        //console.log(`Updated tool display: Slot ${slotNumber} = ${toolItemCode || 'empty'}`);
        this.updateDebugVisuals();
    }
    
    createEquippedIcon(itemCode, x, y) {
        let textureKey;
        
        // Determine texture key based on item code
        if (itemCode.startsWith('Tool_')) {
            // Tool item codes: Tool_0, Tool_1, etc. (from items.json format)
            // Direct mapping for reliable tool texture selection
            const directToolMap = {
                'Tool_0': 'tool-hoe',        // Hoe
                'Tool_1': 'tool-watering-can', // Watering Can
                'Tool_2': 'tool-axe',        // Axe  
                'Tool_3': 'tool-pickaxe',    // Pickaxe
                'Tool_4': 'tool-scythe'      // Scythe
            };
            
            textureKey = directToolMap[itemCode] || 'tool-hoe';
        } else if (itemCode.startsWith('Seeds_')) {
            // Seed item codes: Seeds_0, Seeds_1, etc.
            const seedData = this.getSeedDataById(itemCode);
            if (seedData) {
                // Use ItemManager to create proper seed bag texture (frame 2)
                const seedItemData = {
                    textureKey: seedData.textureKey,
                    type: 'Seeds'
                };
                const seedTextureKey = itemManager.createSeedBagTexture(this.scene, seedItemData);
                if (seedTextureKey) {
                    textureKey = seedTextureKey;
                } else {
                    console.warn('Failed to create seed bag texture for:', seedData.textureKey);
                    textureKey = seedData.textureKey; // fallback to raw texture
                }
            } else {
                console.warn('No seed data found for:', itemCode);
                textureKey = 'tomato'; // fallback
            }
        } else {
            console.warn(`Unknown item code: ${itemCode}`);
            textureKey = 'tool-hoe'; // fallback
        }
        
        // Create the icon sprite
        const icon = this.scene.add.image(x, y, textureKey);
        icon.setDepth(510); // Above hover overlay (508) but below interaction areas (512)
        icon.setScrollFactor(0);
        
        // Make disabled tools (axe and pickaxe) transparent
        const disabledTools = ['Tool_2', 'Tool_3']; // Axe and Pickaxe
        if (disabledTools.includes(itemCode)) {
            icon.setAlpha(0.3);
        } else {
            // Apply normal transparency if ToolUI is currently transparent
            icon.setAlpha(this.currentAlpha);
        }
        
        // Store item code for tooltip functionality
        icon.itemCode = itemCode;
        
        // Don't make icon interactive - use slot areas for consistent hover detection
        
        return icon;
    }
    
    createQuantityText(itemCode, x, y) {
        // Get quantity from inventory
        const quantity = this.getItemQuantity(itemCode);
        
        // Create quantity text
        const quantityText = this.scene.add.text(x, y, quantity.toString(), {
            fontSize: '8px',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 1,
            fontStyle: 'bold'
        });
        quantityText.setOrigin(0.5, 0.5);
        quantityText.setScrollFactor(0);
        quantityText.setDepth(511); // Above icon but below interaction areas
        quantityText.setAlpha(this.currentAlpha);
        
        return quantityText;
    }
    
    getItemQuantity(itemCode) {
        // Get quantity from inventory manager
        const inventory = inventoryManager.getInventory();
        const item = inventory.find(item => item.itemCode === itemCode);
        return item ? item.quantity : 0;
    }
    
    updateSeedQuantity(itemCode, newQuantity) {
        // Update quantity text for equipped seedbags
        if (this.equippedSlot1 === itemCode && this.slot1QuantityText) {
            this.slot1QuantityText.setText(newQuantity.toString());
            
            // Auto-unequip if quantity reaches 0
            if (newQuantity <= 0) {
                this.unequipTool(1);
            }
        }
        
        if (this.equippedSlot2 === itemCode && this.slot2QuantityText) {
            this.slot2QuantityText.setText(newQuantity.toString());
            
            // Auto-unequip if quantity reaches 0
            if (newQuantity <= 0) {
                this.unequipTool(2);
            }
        }
    }
    
    getSeedDataById(seedId) {
        // Extract the numeric ID from Seeds_X format
        const numericId = seedId.replace('Seeds_', '');
        
        // Find the seed in itemManager
        const itemData = itemManager.getItemData(seedId);
        if (!itemData) {
            console.warn('Seed not found in itemManager:', seedId);
            return null;
        }
        
        return {
            id: numericId,
            textureKey: itemData.textureKey,
            name: itemData.name,
            description: itemData.description,
            price: itemData.value || 0,
            farmingLevelRequired: itemData.farmingLevelRequired || 0
        };
    }

    getToolTooltipText(toolId) {
        const disabledTools = ['Tool_2', 'Tool_3']; // Axe and Pickaxe
        
        if (disabledTools.includes(toolId)) {
            return 'Coming Soon';
        } else {
            // Get tool name from items.json
            const toolData = itemManager.getItemData(toolId);
            return toolData ? toolData.name : 'Tool';
        }
    }
    
    setupTooltip() {
        this.tooltip = this.scene.add.container(0, 0);
        this.tooltip.setAlpha(0.9);
        this.tooltip.setDepth(520); // Above everything else
        this.tooltip.setScrollFactor(0);
        
        this.tooltipBg = this.scene.add.rectangle(0, 0, 100, 20, 0x78716C)
            .setOrigin(0)
            .setStrokeStyle(2, 0xffffff);
        this.tooltip.add(this.tooltipBg);
        
        this.tooltipText = this.scene.add.text(3, 3, '', { 
            fontSize: '12px',
            fontStyle: 'bold',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        this.tooltip.add(this.tooltipText);
        this.tooltip.setVisible(false);
    }
    

    
    showTooltip(icon, text, slotNumber) {
        if (!icon) return; // Safety check
        
        this.tooltipText.setText(text);
        
        const textWidth = this.tooltipText.width;
        this.tooltipBg.setSize(textWidth + 6, 20);
        
        // Position tooltip above the icon
        let tooltipX = icon.x - (textWidth / 2);
        let tooltipY = icon.y - 30;
        
        // Special adjustment for slot 2 (right slot) - shift left by 5px
        if (slotNumber === 2) {
            tooltipX -= 5;
        }
        
        // Keep tooltip within viewport bounds
        const UI_WIDTH = 256;
        const UI_HEIGHT = 192;
        
        if (tooltipX < 5) tooltipX = 5;
        if (tooltipX + textWidth > UI_WIDTH - 5) tooltipX = UI_WIDTH - textWidth - 5;
        if (tooltipY < 5) tooltipY = icon.y + 20; // Show below if no room above
        
        this.tooltip.setPosition(tooltipX, tooltipY);
        this.tooltip.setVisible(true);
    }
    
    // Method to get currently equipped tools
    getEquippedTools() {
        return {
            slot1: this.equippedSlot1,
            slot2: this.equippedSlot2
        };
    }
    equipTool(slotNumber, toolId) {
        //console.log(`Equipping tool ${toolId} to slot ${slotNumber}`);
        // TODO: Implement tool equipping logic
        // This will show the tool icon in the appropriate slot
    }
    
    // Method to update the UI when tools change
    updateToolDisplay() {
        // TODO: Update the visual display of equipped tools
        // This will show the correct tool icons in each slot
    }
    
    setupTransparencySystem() {
        // Set up regular update checks for transparency
        this.scene.time.addEvent({
            delay: 100, // Check every 100ms
            callback: this.updateTransparency,
            callbackScope: this,
            loop: true
        });
    }
    
    updateTransparency() {
        const shouldBeTransparent = this.shouldMakeTransparent();
        
        if (shouldBeTransparent !== this.isTransparent) {
            this.isTransparent = shouldBeTransparent;
            this.targetAlpha = shouldBeTransparent ? 0.3 : 1.0;
            
            // Smooth transition
            this.scene.tweens.add({
                targets: this,
                currentAlpha: this.targetAlpha,
                duration: 300,
                ease: 'Power2',
                onUpdate: () => {
                    this.updateUIAlpha();
                }
            });
        }
    }
    
    shouldMakeTransparent() {
        const camera = this.scene.cameras.main;
        const player = this.scene.player;
        
        if (!player) return false;
        
        // Scene bounds: 512x384, viewport: 256x192
        const maxCameraX = 512 - 256; // 256
        const maxCameraY = 384 - 192; // 192
        
        // Check if camera is in bottom right quadrant (within 80 pixels of max scroll)
        const isBottomRightCamera = camera.scrollX > (maxCameraX - 80) && camera.scrollY > (maxCameraY - 80);
        
        // Check if player is in bottom portion of scene (Y > 300)
        const isPlayerAtBottom = player.y > 300;
        
        return isBottomRightCamera && isPlayerAtBottom;
    }
    
    updateUIAlpha() {
        // Apply alpha to all UI elements
        this.toolUIBg.setAlpha(this.currentAlpha);
        this.slot1Hover.setAlpha(this.currentAlpha);
        this.slot2Hover.setAlpha(this.currentAlpha);
        
        // Apply alpha to equipped tool icons
        if (this.slot1Icon) {
            this.slot1Icon.setAlpha(this.currentAlpha);
        }
        if (this.slot2Icon) {
            this.slot2Icon.setAlpha(this.currentAlpha);
        }
        
        // Apply alpha to tooltip
        if (this.tooltip) {
            this.tooltip.setAlpha(this.currentAlpha * 0.9); // Keep slightly more transparent
        }
        
        // Debug graphics and text don't need transparency (they're debug tools)
        if (!this.debugMode) {
            if (this.debugGraphics) {
                this.debugGraphics.setAlpha(this.currentAlpha);
            }
        }
    }
    
    destroy() {
        if (this.debugText) {
            this.debugText.destroy();
        }
        // Clean up interactive areas (not children of container)
        if (this.slot1Area) {
            this.slot1Area.destroy();
        }
        if (this.slot2Area) {
            this.slot2Area.destroy();
        }
        // Clean up equipped tool icons (not children of container)
        if (this.slot1Icon) {
            this.slot1Icon.destroy();
        }
        if (this.slot2Icon) {
            this.slot2Icon.destroy();
        }
        // Clean up debug graphics (not child of container)
        if (this.debugGraphics) {
            this.debugGraphics.destroy();
        }
        // Clean up tooltip
        if (this.tooltip) {
            this.tooltip.destroy();
        }
        super.destroy();
    }
} 